import { create } from 'zustand';
import {
  ALL_REGIONS,
  AutomationMessageApi, ClientExecutiveApi, CountryApi, CourseApi, CurriculumApi,
  CustomFieldApi, FacilityApi, FlagDefinitionApi, HolidayApi, InstructorApi, KlassApi,
  LanguageApi, PartnerApi, PipelineKlassApi, PrivateCourseApi, RedHatRegionApi, SavedFilterApi,
  TrainingSpecialistStrategicApi
} from '@app/api';
import { toApiError } from '@app/utils/ApiError';
import {
  AUTOMATION_MESSAGE_STORE_PAGE_SIZE, ENTITY_STORE_PAGE_SIZE,
  HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE, HOLIDAY_STORE_PAGE_SIZE,
  KLASS_STORE_PAGE_SIZE
} from '@app/utils/config';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';

/**
 * Watches for the one failure mode the request sizes in utils/config.ts can produce: a collection
 * outgrowing the single request its store uses to load it. The endpoint answers with the first N
 * rows, the store keeps them, and every page built on it is quietly missing the tail — no error,
 * nothing in the console, just numbers that are wrong.
 *
 * So each collection is asked how many rows it actually holds and the answer compared with the
 * size we ask for. Cheap: one integer per collection, once per session. If any of them is over,
 * DataCompletenessBanner says so, and the fix is the escalation path documented in config.ts.
 *
 * Deliberately advisory. Nothing here blocks, retries or corrects anything — a collection that
 * cannot be measured is skipped, because a diagnostic that breaks the application it is
 * diagnosing is worse than the silence it was meant to replace.
 */

/** What a collection can tell us about its size, relative to the number of rows we ask for. */
type Measurement =
  /** Everything fits: the store is holding the whole collection. */
  | { fits: true }
  /** Over the size. `total` is the real row count, or null when only "more than that" is knowable. */
  | { fits: false; total: number | null };

interface Collection {
  /** As it should read in the banner, so the reader can tell which pages are affected. */
  label: string;
  /** Rows the store asks for. */
  size: number;
  measure: (size: number) => Promise<Measurement>;
  /**
   * Set where the whole resource is admin-only. A non-admin's store never loads it, so there is
   * nothing to be truncated for them and asking only earns a 403 in their console.
   */
  adminOnly?: boolean;
}

/**
 * The ordinary case: /count gives the exact total, and the store asks for it unfiltered.
 *
 * `params` is for the store that does filter, and has to be the same filter its own load
 * carries. A count taken without it measures a different collection - the holidays differ
 * several-fold between one region and all of them - and the banner would report a page as
 * truncated on every sign-in.
 */
const counted = (
  api: { count: (params?: Record<string, string>) => Promise<number> },
  params?: Record<string, string>
) => async (size: number): Promise<Measurement> => {
  const total = await api.count(params);
  return total > size ? { fits: false, total } : { fits: true };
};

/**
 * Classes are the exception: the store asks for a date window, which /class/count cannot express.
 * See KlassApi.exceedsWindow — it establishes that the window is over the size without being able
 * to say by how much, hence the null total.
 */
const classWindow = async (size: number): Promise<Measurement> =>
  (await KlassApi.exceedsWindow(size)) ? { fits: false, total: null } : { fits: true };

/**
 * Every collection loaded in one request. Alphabetical, apart from classes leading because it is
 * the one that grows without bound.
 *
 * A store added without a line here simply is not watched, which is the state everything was in
 * before this existed — so this list is worth checking when a store is added.
 *
 * Not here, and why: the Calendar's per-week class fetch and its per-country holiday fetch
 * (CALENDAR_WEEK_PAGE_SIZE) are already narrowed by week and by country, and neither filter can be
 * expressed as a /count; the parameter catalog is a fixed handful of rows and is not paged at all.
 */
const COLLECTIONS: Collection[] = [
  { label: 'Classes', size: KLASS_STORE_PAGE_SIZE, measure: classWindow },
  { label: 'Automation messages', size: AUTOMATION_MESSAGE_STORE_PAGE_SIZE, measure: counted(AutomationMessageApi) },
  { label: 'Client executives', size: ENTITY_STORE_PAGE_SIZE, measure: counted(ClientExecutiveApi) },
  { label: 'Countries', size: ENTITY_STORE_PAGE_SIZE, measure: counted(CountryApi) },
  { label: 'Courses', size: ENTITY_STORE_PAGE_SIZE, measure: counted(CourseApi) },
  { label: 'Curricula', size: ENTITY_STORE_PAGE_SIZE, measure: counted(CurriculumApi) },
  { label: 'Custom fields', size: ENTITY_STORE_PAGE_SIZE, measure: counted(CustomFieldApi) },
  { label: 'Facilities', size: ENTITY_STORE_PAGE_SIZE, measure: counted(FacilityApi) },
  { label: 'Flags', size: ENTITY_STORE_PAGE_SIZE, measure: counted(FlagDefinitionApi), adminOnly: true },
  { label: 'Holidays', size: HOLIDAY_STORE_PAGE_SIZE, measure: counted(HolidayApi) },
  // The administration screen's copy, which asks for every subdivision's days rather than the
  // nationwide ones - a different collection, so a size and a count of its own. See
  // HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE.
  {
    label: 'Holidays (all regions)',
    size: HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE,
    measure: counted(HolidayApi, { region: ALL_REGIONS }),
    adminOnly: true
  },
  { label: 'Instructors', size: ENTITY_STORE_PAGE_SIZE, measure: counted(InstructorApi) },
  { label: 'Languages', size: ENTITY_STORE_PAGE_SIZE, measure: counted(LanguageApi) },
  { label: 'Partners', size: ENTITY_STORE_PAGE_SIZE, measure: counted(PartnerApi) },
  // Grows without bound like classes do, but countable: a request has no date window to filter by,
  // so its store asks for all of them and /pipelineclass/count answers exactly.
  { label: 'Pipeline requests', size: ENTITY_STORE_PAGE_SIZE, measure: counted(PipelineKlassApi) },
  { label: 'Private courses', size: ENTITY_STORE_PAGE_SIZE, measure: counted(PrivateCourseApi) },
  { label: 'Red Hat regions', size: ENTITY_STORE_PAGE_SIZE, measure: counted(RedHatRegionApi) },
  { label: 'Saved filters', size: ENTITY_STORE_PAGE_SIZE, measure: counted(SavedFilterApi) },
  { label: 'Training specialists', size: ENTITY_STORE_PAGE_SIZE, measure: counted(TrainingSpecialistStrategicApi) },
];

/** A collection holding more rows than its store can load. */
export interface Truncation {
  label: string;
  /** Rows the store asks for, and therefore the most it can be holding. */
  size: number;
  /** Rows that exist, or null when all that could be established is "more than `size`". */
  total: number | null;
}

interface DataCompletenessStore {
  /** Collections found to be over their size, by label. Empty is the healthy case. */
  truncations: Truncation[];
  /**
   * Whether a check has managed to measure anything. False after a run during which every
   * collection failed — a service that was down throughout — so it can be tried again.
   */
  hasRun: boolean;
  /** Measures every collection and records what is over. Never rejects. */
  check: () => Promise<void>;
}

// Outside the store: a shared request, not state. Two mounts must not run seventeen counts twice.
let inFlight: Promise<void> | null = null;

export const useDataCompletenessStore = create<DataCompletenessStore>((set) => ({
  truncations: [],
  hasRun: false,

  check: async () => {
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        const isAdmin = hasRealmRole(ROLE.ADMIN);
        const applicable = COLLECTIONS.filter((c) => !c.adminOnly || isAdmin);

        const found: Truncation[] = [];
        let measured = 0;

        await Promise.all(applicable.map(async (collection) => {
          try {
            const measurement = await collection.measure(collection.size);
            measured++;
            if (!measurement.fits) {
              found.push({ label: collection.label, size: collection.size, total: measurement.total });
            }
          } catch (err) {
            // Left alone rather than reported: the service may be down, or this user may not be
            // allowed to count this collection. Neither is evidence of truncation, and neither is
            // worth interrupting anyone over.
            console.debug(`[Completeness] Could not measure ${collection.label}:`, toApiError(err).message);
          }
        }));

        // Concurrent pushes land in completion order; sorting keeps the banner stable between runs.
        found.sort((a, b) => a.label.localeCompare(b.label));

        if (found.length > 0) {
          console.error(
            '[Completeness] These collections hold more rows than this application loads, so the ' +
            'pages built on them are missing rows. Raise the sizes in utils/config.ts:',
            found
          );
        }

        set({ truncations: found, hasRun: measured > 0 });
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  }
}));
